/**
 * ReceiptItemRow — T146 + T146P2 (US1-US6)
 *
 * Single receipt-style row primitive consumed by TransactionLineItems, MobileItemsSheet,
 * TransactionDetailSheet, and TransactionForm.
 *
 * T146P2 enhancements:
 * - density prop (compact|receipt|detailed) controls field visibility
 * - columnVisibility prop hides columns via CSS (emoji, qty, category, notes)
 * - isEditable prop enables inline qty/amount editing + delete button
 * - onItemChange callback for qty/amount changes
 * - onItemDelete callback for delete action
 * - onCategoryChange callback for per-item category override (detailed mode only)
 *
 * Visual: emoji · description · line total (receipt)
 *         (qty × unit = total) microline always shown in view mode (T226PY)
 *         category chip: rounded-full colored-circle + emoji + name (parent-style, T226PY)
 *         notes microline (italic, line-clamp-1)
 *
 * Pattern: shadcn/ui (Input, Select) + Tailwind utility classes only.
 * @see https://tailwindcss.com/docs
 */
import { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { IconButton } from '@/components/ui/icon-button';
import { FileText, Hash, Percent, Tag, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';
import type { TransactionItem, Category } from '@/lib/types';
import { getItemBreakdown } from '@/lib/transactionBreakdown';

interface ReceiptItemRowProps {
  item: TransactionItem;
  /** ISO 4217 currency code (passed for parity, fmt resolves from auth store). */
  currency?: string;
  /** Optional sequential row label rendered as "01.", "02." */
  rowNumber?: number;
  className?: string;
  
  // T146P2: Display density (compact|receipt|detailed)
  density?: 'compact' | 'receipt' | 'detailed';
  
  // T146P2: Column visibility toggles
  columnVisibility?: {
    emoji?: boolean;
    qty?: boolean;
    category?: boolean;
    notes?: boolean;
  };
  
  // T146P2: Editable mode (TransactionForm only)
  isEditable?: boolean;
  index?: number;
  transactionCategory?: string;
  categories?: Category[];
  onItemChange?: (index: number, field: 'qty' | 'amount', value: number) => void;
  onItemDelete?: (index: number) => void;
  onCategoryChange?: (index: number, categoryId: string | undefined) => void;
}


export default function ReceiptItemRow({
  item,
  rowNumber,
  className,
  density = 'receipt',
  columnVisibility = {
    emoji: true,
    qty: true,
    category: true,
    notes: true,
  },
  isEditable = false,
  index = 0,
  transactionCategory,
  categories = [],
  onItemChange,
  onItemDelete,
  onCategoryChange,
}: ReceiptItemRowProps) {
  const { fmt } = useCurrency();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingField, setEditingField] = useState<'qty' | 'amount' | null>(null);
  const [editValue, setEditValue] = useState('');
  const canDelete = isEditable && typeof onItemDelete === 'function';

  // R27-S: Consume shared breakdown helper; do not duplicate gross/discount/net formulas.
  const breakdown = getItemBreakdown(item);
  const qty = breakdown.quantity;
  const unit = item.amount;
  const grossLineTotal = breakdown.gross;
  const lineDiscount = breakdown.discount;
  const lineTotal = breakdown.net;
  const hasLineDiscount = typeof lineDiscount === 'number' && lineDiscount > 0;
  const isIncomplete = !breakdown.complete;
  const hasCategory = !!(item.categoryName || item.categoryEmoji || item.categoryId);
  const categoryOverride = !!(item.categoryId && item.categoryId !== transactionCategory);
  // R27-S: Preserve null as explicit "Unavailable" text instead of coercing to 0/1.
  const UNAVAILABLE = 'Unavailable';
  const showValue = (v: number | null) => (v == null ? UNAVAILABLE : fmt(v));
  const showMetadataLane =
    (columnVisibility.category !== false && (density === 'detailed' || hasCategory)) ||
    (columnVisibility.notes !== false && !!item.notes) ||
    (columnVisibility.qty !== false && !isEditable) ||
    hasLineDiscount;

  // All hooks must be called in the same order, so define them before any early returns
  const handleQtyEdit = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const newValue = parseFloat(e.currentTarget.value);
      if (!isNaN(newValue) && onItemChange) {
        onItemChange(index, 'qty', newValue);
      }
      setEditingField(null);
    },
    [index, onItemChange]
  );

  const handleAmountEdit = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const newValue = parseFloat(e.currentTarget.value);
      if (!isNaN(newValue) && onItemChange) {
        onItemChange(index, 'amount', newValue);
      }
      setEditingField(null);
    },
    [index, onItemChange]
  );

  const handleDelete = useCallback(() => {
    if (onItemDelete) {
      onItemDelete(index);
    }
    setShowDeleteConfirm(false);
  }, [index, onItemDelete]);

  // Compact density: hide everything except qty + amount
  if (density === 'compact') {
    return (
      <div
        data-testid="receipt-item-row"
        className={cn('flex items-center gap-2 py-1 min-h-[44px] text-xs', className)}
      >
        {columnVisibility.qty !== false && (
          <span className="text-muted-foreground w-8">{showValue(qty)}x</span>
        )}
        {columnVisibility.emoji !== false && (
          <span className="shrink-0 inline-flex items-center justify-center w-5 h-5 text-sm">
            {item.emoji || '📄'}
          </span>
        )}
        <span className="flex-1 min-w-0 text-muted-foreground break-words [overflow-wrap:anywhere] leading-snug">{item.description}</span>
        <span className="tabular-nums font-semibold whitespace-nowrap">
          {showValue(lineTotal)}{hasLineDiscount ? ' net' : ''}
        </span>
      </div>
    );
  }

  return (
    <>
      <div
        data-testid="receipt-item-row"
        className={cn(
          'grid items-center gap-x-2 gap-y-0 min-h-[44px] relative rounded-xl border border-border/60 bg-card/60 px-3 py-2 shadow-sm',
          rowNumber != null
            ? 'grid-cols-[20px_24px_minmax(7rem,1fr)_auto]'
            : 'grid-cols-[24px_minmax(7rem,1fr)_auto]',
          categoryOverride && 'bg-amber-50 dark:bg-amber-950/20',
          className
        )}
      >
        {/* Col 1: Row number (own column — separate from description) */}
        {rowNumber != null && (
          <span className="text-[10px] tabular-nums text-muted-foreground/70 text-right leading-none self-center">
            {String(rowNumber).padStart(2, '0')}.
          </span>
        )}

        {/* Col 2: Emoji — invisible placeholder preserves grid column when hidden */}
        <span
          data-testid="item-col-emoji"
          aria-hidden="true"
          className={cn(
            'shrink-0 inline-flex items-center justify-center w-6 h-6 text-base leading-none self-center',
            columnVisibility.emoji === false && 'invisible',
          )}
        >
          {item.emoji || '📄'}
        </span>

        {/* Col 3: Description + meta (mirrors parent description column) */}
        <div className="min-w-0 max-w-full space-y-0.5">
          {/* Primary description line */}
          <div className="text-sm font-medium break-words [overflow-wrap:anywhere] leading-snug">
            {item.description}
          </div>
          {/* R27-S rev2 cleanup: detailed read-only incomplete breakdown message */}
          {density === 'detailed' && !isEditable && isIncomplete && (
            <span data-testid="item-breakdown-incomplete">Incomplete item data</span>
          )}

          {/* Editable fields row (detailed density only in form) */}
          {isEditable && density === 'detailed' && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <label className="text-[10px] text-muted-foreground block mb-0.5">Qty</label>
                <Input
                  type="number"
                  min="0.01"
                  max="999.99"
                  step="0.01"
                  value={editingField === 'qty' ? editValue : (qty ?? '')}
                  onFocus={() => {
                    setEditingField('qty');
                    setEditValue(qty != null ? String(qty) : '');
                  }}
                  onChange={(e) => setEditValue(e.currentTarget.value)}
                  onBlur={handleQtyEdit}
                  className="h-[44px] text-xs"
                />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground block mb-0.5">Amount</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editingField === 'amount' ? editValue : (unit ?? '')}
                  onFocus={() => {
                    setEditingField('amount');
                    setEditValue(unit != null ? String(unit) : '');
                  }}
                  onChange={(e) => setEditValue(e.currentTarget.value)}
                  onBlur={handleAmountEdit}
                  className="h-[44px] text-xs"
                />
              </div>
            </div>
          )}

          {/* Category override indicator (detailed mode) */}
          {density === 'detailed' && categoryOverride && (
            <span
              data-testid={`item-category-indicator-${index}`}
              className="text-[10px] text-amber-600 dark:text-amber-500 italic"
            >
              * Custom category override
            </span>
          )}

          {/* Single metadata lane: category → notes → qty×unit → discount */}
          {showMetadataLane && (
            <div
              data-testid="item-col-metadata"
              className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px] leading-tight text-muted-foreground"
            >
              {columnVisibility.category !== false && (
                density === 'detailed' && isEditable ? (
                  <div data-testid="item-col-category" className="inline-flex min-w-0 max-w-full items-center gap-1">
                    <Tag className="h-3 w-3 shrink-0" aria-hidden="true" />
                    <Select
                      value={item.categoryId || 'inherit'}
                      onValueChange={(val) => onCategoryChange?.(index, val === 'inherit' ? undefined : val)}
                    >
                      <SelectTrigger data-testid={`item-category-select-${index}`} className="h-8 text-xs w-full">
                        <SelectValue placeholder="Select category..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inherit">Same as transaction</SelectItem>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.emoji} {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : hasCategory ? (
                  <span
                    data-testid="item-col-category"
                    className="inline-flex min-w-0 items-center gap-1 rounded-md bg-muted/50 px-1.5 py-0.5"
                  >
                    <span
                      className="inline-flex h-4 w-4 rounded-full items-center justify-center text-[10px] shrink-0"
                      style={{
                        backgroundColor: item.categoryColor ? `${item.categoryColor}20` : '#e5e7eb',
                      }}
                      aria-hidden="true"
                    >
                      {item.categoryEmoji ?? '🏷️'}
                    </span>
                    {item.categoryName && (
                      <span className="min-w-0 break-words [overflow-wrap:anywhere] text-[11px] text-muted-foreground">
                        {item.categoryName}
                      </span>
                    )}
                  </span>
                ) : null
              )}

              {columnVisibility.notes !== false && item.notes && (
                <span
                  data-testid="item-col-notes"
                  className="inline-flex min-w-0 max-w-full items-start gap-1 break-words [overflow-wrap:anywhere] italic"
                >
                  <FileText className="h-3 w-3 shrink-0 not-italic" aria-hidden="true" />
                  <span className="min-w-0 break-words [overflow-wrap:anywhere]">{item.notes}</span>
                </span>
              )}

              {columnVisibility.qty !== false && !isEditable && (
                <span
                  data-testid="item-col-qty"
                  className={cn(
                    'inline-flex items-center gap-1 tabular-nums whitespace-nowrap',
                    qty != null && qty > 1 ? 'text-muted-foreground/90' : 'text-muted-foreground/60',
                  )}
                >
                  <Hash className="h-3 w-3 shrink-0" aria-hidden="true" />
                  {showValue(qty)} × {showValue(unit)}
                </span>
              )}

              {hasLineDiscount && (
                <span
                  data-testid="item-col-discount"
                  className="inline-flex items-center gap-1 tabular-nums whitespace-nowrap text-emerald-600 dark:text-emerald-400"
                >
                  <Percent className="h-3 w-3 shrink-0" aria-hidden="true" />
                  {fmt(lineDiscount)}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Col 4: Amount cluster (mirrors parent TransactionCard) + delete button */}
        <div className="flex items-center gap-0.5 self-center">
          {/* Amount — hidden in editable mode (user enters via inputs) */}
          {!isEditable && (
            <div className="min-w-[3rem] space-y-0.5 tabular-nums text-right leading-tight">
              <div data-testid="item-col-net" className="text-sm font-semibold whitespace-nowrap">
                {showValue(lineTotal)}
              </div>
              {/* R27-S rev2: gross shown for any complete detailed row; discount remains under hasLineDiscount */}
              {density === 'detailed' && grossLineTotal !== null && (
                <div className="flex flex-col items-end gap-0.5 text-muted-foreground">
                  <span
                    data-testid="item-col-gross"
                    className="whitespace-nowrap text-[10px] sm:text-[11px] leading-none"
                    title="Gross amount"
                  >
                    {fmt(grossLineTotal)}
                  </span>
                  {hasLineDiscount && (
                    <span
                      className="whitespace-nowrap text-[10px] sm:text-[11px] leading-none text-emerald-600 dark:text-emerald-400"
                      title="Discount applied"
                    >
                      {fmt(lineDiscount)}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Delete button (editable mode only) */}
          {canDelete && (
            <IconButton
              data-testid={`item-delete-btn-${index}`}
              variant="ghost"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              tooltip="Delete item"
              className="h-[44px] w-[44px] shrink-0"
            >
              <Trash2 className="h-4 w-4" />
            </IconButton>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete item?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
